import { Card, IconSvg, Main_Plain } from 'monica-alexandria';
import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { offer_overview_path } from './offers_board_rules';

const OffersBoardCard = styled(Card)`
    padding: 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    background: linear-gradient(180deg, ${p => p.theme.background} 0%, ${p => p.theme.low} 100%);
    cursor: pointer;
`;

const OffersBoardCardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.8rem;
`;

const OffersBoardCardTitle = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
`;

const OffersBoardCardType = styled.h6`
    align-self: flex-start;
    margin: 0;
    padding: 0.3rem 0.7rem;
    border-radius: var(--smallRadius);
    background: ${p => p.theme.mid};
    color: ${p => p.theme.color};
`;

const OffersBoardCardFake = styled.h6`
    align-self: flex-start;
    margin: 0;
    padding: 0.3rem 0.7rem;
    border-radius: var(--smallRadius);
    background: var(--warning);
    color: ${p => p.theme.background};
    text-transform: uppercase;
`;

const OffersBoardCardLabels = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
`;

const OffersBoardCardMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    align-items: flex-start;

    h6 {
        margin: 0;
        color: ${p => p.theme.color};
    }
`;

const OffersBoardCardMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    max-width: 100%;
    color: ${p => p.theme.color};

    svg {
        width: 1.4rem;
        height: 1.4rem;
        flex-shrink: 0;
        fill: currentColor;
    }

    h6 {
        overflow-wrap: anywhere;
        min-width: 0;
    }
`;

export default function Offers_Board_Card(props) {
    const navigate = useNavigate();
    const previewOfferId = `${props.previewOfferId || ''}`.trim();
    const routeLanguage = props.routeLanguage || 'el';

    const open_offer_workflow = () => {
        if (!previewOfferId.length) {
            return;
        }

        if (props.cerebroHandoff) {
            if (props.cerebroTaskUrl) {
                window.open(props.cerebroTaskUrl, '_blank', 'noopener,noreferrer');
            }
            return;
        }

        if (props.continueOffer) {
            navigate(`/${routeLanguage}/new-offer?offerId=${encodeURIComponent(previewOfferId)}&continueOffer=1`);
            return;
        }

        window.open(`/${routeLanguage}/offer/${encodeURIComponent(previewOfferId)}`, '_blank', 'noopener,noreferrer');
    };

    const open_offer_preview = () => {
        if (props.viewOnly) {
            const overviewPath = offer_overview_path(previewOfferId, routeLanguage);

            if (overviewPath.length) {
                navigate(overviewPath);
            }
            return;
        }

        open_offer_workflow();
    };

    const open_offer_preview_from_keyboard = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        open_offer_preview();
    };

    const open_offer_action = (event) => {
        event.stopPropagation();
        open_offer_workflow();
    };

    const retry_cerebro_sync = (event) => {
        event.stopPropagation();
        props.onRetryPreofferSync(props.offerItem);
    };

    const edit_fake_offer = (event) => {
        event.stopPropagation();
        props.onEditFakeOffer(props.offerItem);
    };

    const delete_fake_offer = (event) => {
        event.stopPropagation();
        props.onDeleteFakeOffer(props.offerItem);
    };

    return (
        <OffersBoardCard
            role="button"
            tabIndex={0}
            onClick={open_offer_preview}
            onKeyDown={open_offer_preview_from_keyboard}
        >
            <OffersBoardCardHeader>
                <OffersBoardCardTitle>
                    <h6>{props.offerReference}</h6>
                    <p>{props.offerBrand}</p>
                </OffersBoardCardTitle>
            </OffersBoardCardHeader>
            {props.offerType?.length || props.fakeOffer ? (
                <OffersBoardCardLabels>
                    {props.offerType?.length ? <OffersBoardCardType>{props.t(props.offerType)}</OffersBoardCardType> : null}
                    {props.fakeOffer ? <OffersBoardCardFake>{props.t('Fake')}</OffersBoardCardFake> : null}
                </OffersBoardCardLabels>
            ) : null}
            <OffersBoardCardMeta>
                {props.offerItem?.setupData?.serviceActivation?.state ? <h6>{props.t(props.offerItem.setupData.serviceActivation.state === 'replaced' ? 'Old / Canceled' : props.offerItem.setupData.serviceActivation.state === 'active' ? 'Active services' : 'Services stopped')}</h6> : null}
                {props.offerEmail?.length ? (
                    <OffersBoardCardMetaRow title={props.t('Email')}>
                        <IconSvg Icon="Email" aria-hidden="true" />
                        <h6>{props.offerEmail}</h6>
                    </OffersBoardCardMetaRow>
                ) : null}
                <OffersBoardCardMetaRow title={props.t('Submitted by')}>
                    <IconSvg Icon="User" aria-hidden="true" />
                    <h6>{props.submitterLabel || '-'}</h6>
                </OffersBoardCardMetaRow>
                {props.reviewerLabel ? (
                    <OffersBoardCardMetaRow title={props.t('Reviewer')}>
                        <IconSvg Icon="EyeOpen" aria-hidden="true" />
                        <h6>{props.reviewerLabel}</h6>
                    </OffersBoardCardMetaRow>
                ) : null}
                {props.creatorLabel ? (
                    <OffersBoardCardMetaRow title={props.t('Creator')}>
                        <IconSvg Icon="Edit" aria-hidden="true" />
                        <h6>{props.creatorLabel}</h6>
                    </OffersBoardCardMetaRow>
                ) : null}
                {props.showVisits ? (
                    <OffersBoardCardMetaRow title={props.t('Visits')}>
                        <IconSvg Icon="EyeOpen" aria-hidden="true" />
                        <h6>{props.visitLabel}</h6>
                    </OffersBoardCardMetaRow>
                ) : null}
                {props.actionText?.length ? (
                    <Main_Plain noPadding text={props.t(props.actionText)} onClick={open_offer_action} />
                ) : null}
                {props.fakeOffer && props.canManageFakeOffers ? (
                    <>
                        <Main_Plain noPadding text={props.t('Create replacement offer')} onClick={edit_fake_offer}></Main_Plain>
                        <Main_Plain
                            noPadding
                            text={props.isDeletingFakeOffer ? props.t('Deleting...') : props.t('Delete fake offer')}
                            disabled={props.isDeletingFakeOffer}
                            onClick={delete_fake_offer}
                        ></Main_Plain>
                    </>
                ) : null}
                {props.cerebroHandoff && !props.cerebroTaskUrl ? (
                    <>
                        <h6>{props.t('Your offer is saved. Its Tasks assignment is pending.')}</h6>
                        <Main_Plain
                            noPadding
                            text={props.t('Retry Tasks sync')}
                            disabled={props.isRetryingPreofferSync}
                            onClick={retry_cerebro_sync}
                        ></Main_Plain>
                    </>
                ) : null}
            </OffersBoardCardMeta>
        </OffersBoardCard>
    );
};
